import React, { useState, useEffect } from 'react';
import { staffApi } from '../../staffApi';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { XMarkIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const TutorSchedule = () => {
  const [tutors, setTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [scheduleData, setScheduleData] = useState({ schedules: [], timezone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // CRUD operation states
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newSlot, setNewSlot] = useState({
    startTime: '09:00',
    endTime: '10:00',
    isAvailable: true,
    isBooked: false,
    eventName: ''
  });
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestionData, setSuggestionData] = useState(null);

  // Load tutors list on component mount
  useEffect(() => {
    loadTutors();
  }, []);

  // Load schedule data when tutor or month changes
  useEffect(() => {
    if (selectedTutor) {
      loadScheduleData();
    }
  }, [selectedTutor, currentMonth]);

  const loadTutors = async () => {
    try {
      setLoading(true);
      const response = await staffApi.getTutorsForSchedule();
      setTutors(response.tutors || []);
    } catch (error) {
      console.error('Error loading tutors:', error);
      setError('Failed to load tutors list');
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleData = async () => {
    if (!selectedTutor) return;
    
    try {
      setLoading(true);
      setError(null);
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      const response = await staffApi.getTutorSchedule(selectedTutor.id, year, month);
      setScheduleData(response.schedule);
    } catch (error) {
      console.error('Error loading schedule:', error);
      setError('Failed to load tutor schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleTutorChange = (tutorId) => {
    const tutor = tutors.find(t => t.id === parseInt(tutorId));
    setSelectedTutor(tutor);
  };

  const handleMonthChange = (direction) => {
    if (direction === 'prev') {
      setCurrentMonth(subMonths(currentMonth, 1));
    } else {
      setCurrentMonth(addMonths(currentMonth, 1));
    }
  };

  const getDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getScheduleForDate = (date) => {
    const dateKey = getDateKey(date);
    return scheduleData.schedules.find(schedule => schedule.date === dateKey);
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // CRUD Operations
  const convertTo24Hour = (time12) => {
    const [time, period] = time12.split(' ');
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minutes}`;
  };

  const convertTo12Hour = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleDateClick = (date) => {
    if (!selectedTutor) return;
    
    setSelectedDate(date);
    const schedule = getScheduleForDate(date);
    
    if (schedule && schedule.time_slots && schedule.time_slots.length > 0) {
      // Show existing slots for editing
      setShowAddSlot(true);
      setEditingSlot(null);
    } else {
      // Show add slot modal
      setNewSlot({ startTime: '09:00', endTime: '10:00', isAvailable: true, isBooked: false, eventName: '' });
      setShowAddSlot(true);
      setEditingSlot(null);
    }
  };

  const handleAddSlot = async () => {
    if (!selectedTutor || !selectedDate || !newSlot.startTime || !newSlot.endTime) return;
    
    try {
      setLoading(true);
      
      const dateKey = getDateKey(selectedDate);
      const existingSchedule = getScheduleForDate(selectedDate);
      
      const start24 = convertTo24Hour(convertTo12Hour(newSlot.startTime));
      const end24 = convertTo24Hour(convertTo12Hour(newSlot.endTime));
      
      const newSlotData = {
        start_time: start24,
        end_time: end24,
        is_available: newSlot.isAvailable,
        is_booked: newSlot.isBooked,
        event_name: newSlot.eventName || null
      };

      const scheduleData = {
        date: dateKey,
        is_unavailable: false,
        time_slots: [
          ...(existingSchedule?.time_slots || []),
          newSlotData
        ].sort((a, b) => a.start_time.localeCompare(b.start_time))
      };
      
      await staffApi.updateTutorSchedule(selectedTutor.id, scheduleData);
      await loadScheduleData(); // Reload data from API
      
      setShowAddSlot(false);
      setNewSlot({ startTime: '09:00', endTime: '10:00', isAvailable: true, isBooked: false, eventName: '' });
      setSelectedDate(null);
      
    } catch (error) {
      console.error('Error adding slot:', error);
      setError('Failed to add time slot');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSlot = (slot, date) => {
    setEditingSlot(slot);
    setSelectedDate(date);
    setNewSlot({
      startTime: slot.start_time,
      endTime: slot.end_time,
      isAvailable: slot.is_available,
      isBooked: slot.is_booked || false,
      eventName: slot.event_name || ''
    });
    setShowAddSlot(true);
  };

  const handleUpdateSlot = async () => {
    if (!editingSlot || !newSlot.startTime || !newSlot.endTime || !selectedDate) return;
    
    try {
      setLoading(true);
      
      const dateKey = getDateKey(selectedDate);
      const existingSchedule = getScheduleForDate(selectedDate);
      
      const start24 = convertTo24Hour(convertTo12Hour(newSlot.startTime));
      const end24 = convertTo24Hour(convertTo12Hour(newSlot.endTime));
      
      const updatedSchedule = {
        date: dateKey,
        is_unavailable: false,
        time_slots: (existingSchedule?.time_slots || []).map(slot =>
          slot.id === editingSlot.id
            ? { ...slot, start_time: start24, end_time: end24, is_available: newSlot.isAvailable, is_booked: newSlot.isBooked, event_name: newSlot.eventName || null }
            : slot
        ).sort((a, b) => a.start_time.localeCompare(b.start_time))
      };
      
      await staffApi.updateTutorSchedule(selectedTutor.id, updatedSchedule);
      await loadScheduleData(); // Reload data from API
      
      setEditingSlot(null);
      setShowAddSlot(false);
      setNewSlot({ startTime: '09:00', endTime: '10:00', isAvailable: true, isBooked: false, eventName: '' });
      setSelectedDate(null);
      
    } catch (error) {
      console.error('Error updating slot:', error);
      setError('Failed to update time slot');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (slot, date) => {
    if (!window.confirm('Are you sure you want to delete this time slot?')) return;
    
    try {
      setLoading(true);
      
      const dateKey = getDateKey(date);
      const existingSchedule = getScheduleForDate(date);
      
      if (existingSchedule && existingSchedule.time_slots.length === 1) {
        // If this is the only slot, delete the entire schedule
        await staffApi.deleteTutorSchedule(selectedTutor.id, dateKey);
      } else {
        // Remove the specific slot
        const updatedSchedule = {
          date: dateKey,
          is_unavailable: false,
          time_slots: (existingSchedule?.time_slots || []).filter(s => s.id !== slot.id)
        };
        
        await staffApi.updateTutorSchedule(selectedTutor.id, updatedSchedule);
      }
      
      await loadScheduleData(); // Reload data from API
      
    } catch (error) {
      console.error('Error deleting slot:', error);
      setError('Failed to delete time slot');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkUnavailable = async () => {
    if (!selectedTutor || !selectedDate) return;
    
    try {
      setLoading(true);
      
      const dateKey = getDateKey(selectedDate);
      
      const scheduleData = {
        date: dateKey,
        is_unavailable: true,
        time_slots: []
      };
      
      await staffApi.updateTutorSchedule(selectedTutor.id, scheduleData);
      await loadScheduleData(); // Reload data from API
      
      setShowAddSlot(false);
      setSelectedDate(null);
      
    } catch (error) {
      console.error('Error marking unavailable:', error);
      setError('Failed to mark date as unavailable');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddSlot(false);
    setEditingSlot(null);
    setNewSlot({ startTime: '09:00', endTime: '10:00', isAvailable: true, isBooked: false, eventName: '' });
    setSelectedDate(null);
  };

  const copyLastWeek = async () => {
    if (!selectedTutor) return;
    
    try {
      setLoading(true);
      await staffApi.copyLastWeekSchedule(selectedTutor.id);
      await loadScheduleData(); // Reload data from API
    } catch (error) {
      console.error('Error copying last week:', error);
      setError('Failed to copy last week\'s schedule');
    } finally {
      setLoading(false);
    }
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfMonth(currentMonth);
    const calendarEnd = endOfMonth(currentMonth);
    
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    const monthName = format(currentMonth, 'MMMM yyyy');

    return (
      <div className="bg-white rounded-lg shadow">
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <button
            onClick={() => handleMonthChange('prev')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <h2 className="text-lg font-semibold text-gray-900">{monthName}</h2>
          
          <button
            onClick={() => handleMonthChange('next')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-4">
          {/* Days of week header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map(day => {
              const schedule = getScheduleForDate(day);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isToday = isSameDay(day, new Date());
              
              return (
                <div
                  key={day.toISOString()}
                  onClick={() => handleDateClick(day)}
                  className={`p-2 min-h-[100px] border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                    isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                  } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  } ${isToday ? 'text-blue-600' : ''}`}>
                    {format(day, 'd')}
                  </div>
                  
                  {schedule && (
                    <div className="space-y-1">
                      {schedule.is_unavailable ? (
                        <div className="text-xs text-red-600 font-medium">Day Off</div>
                      ) : (
                        schedule.time_slots.slice(0, 3).map((slot, slotIndex) => (
                          <div key={slotIndex} className={`text-xs truncate ${
                            slot.is_booked ? 'text-blue-600 font-medium' : 'text-green-600'
                          }`}>
                            {formatTime(slot.start_time)}-{formatTime(slot.end_time)}
                            {slot.is_booked && (
                              slot.event_name ? ` (${slot.event_name})` : ' (Booked)'
                            )}
                          </div>
                        ))
                      )}
                      {schedule.time_slots.length > 3 && (
                        <div className="text-xs text-gray-400">
                          +{schedule.time_slots.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderEventModal = () => {
    if (!showAddSlot || !selectedDate) return null;

    const schedule = getScheduleForDate(selectedDate);
    const existingSlots = schedule?.time_slots || [];

    return (
      <Dialog open={showAddSlot} onClose={handleCancel} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingSlot ? 'Edit Time Slot' : 'Manage Schedule'}
                </h3>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Date Info */}
              <div className="mb-4 p-3 bg-blue-50 rounded-md">
                <div className="text-sm font-medium text-blue-900">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </div>
                <div className="text-sm text-blue-700">
                  {selectedTutor?.name} - {scheduleData.timezone}
                </div>
              </div>

              {/* Existing Slots */}
              {existingSlots.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Existing Time Slots</h4>
                  <div className="space-y-2">
                    {existingSlots.map((slot, index) => (
                      <div key={slot.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            slot.is_booked ? 'bg-blue-500' : 'bg-green-500'
                          }`}></div>
                          <span className="text-sm text-gray-900">
                            {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                          </span>
                          <span className={`text-xs ${
                            slot.is_booked ? 'text-blue-600' : 'text-green-600'
                          }`}>
                            {slot.is_booked ? (slot.event_name || 'Booked') : 'Available'}
                          </span>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEditSlot(slot, selectedDate)}
                            className="p-1 text-gray-400 hover:text-indigo-600"
                            title="Edit slot"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSlot(slot, selectedDate)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete slot"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Slot Form */}
              {!editingSlot && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                    <select
                      value={newSlot.startTime}
                      onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = i;
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour % 12 || 12;
                        const timeString = `${displayHour}:00 ${ampm}`;
                        return (
                          <option key={i} value={timeString}>
                            {timeString}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                    <select
                      value={newSlot.endTime}
                      onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = i;
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour % 12 || 12;
                        const timeString = `${displayHour}:00 ${ampm}`;
                        return (
                          <option key={i} value={timeString}>
                            {timeString}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Slot Status</label>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: true, isBooked: false })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          newSlot.isAvailable && !newSlot.isBooked
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Available
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: false, isBooked: true })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          newSlot.isBooked && !newSlot.isAvailable
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Booked
                      </button>
                    </div>
                  </div>

                  {/* Event Name Field - Only show for booked slots */}
                  {newSlot.isBooked && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Name (Optional)</label>
                      <input
                        type="text"
                        value={newSlot.eventName}
                        onChange={(e) => setNewSlot({ ...newSlot, eventName: e.target.value })}
                        placeholder="e.g., Math Tutoring Session, Student Meeting"
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Edit slot form */}
              {editingSlot && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Time</label>
                    <select
                      value={newSlot.startTime}
                      onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = i;
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour % 12 || 12;
                        const timeString = `${displayHour}:00 ${ampm}`;
                        return (
                          <option key={i} value={timeString}>
                            {timeString}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Time</label>
                    <select
                      value={newSlot.endTime}
                      onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = i;
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour % 12 || 12;
                        const timeString = `${displayHour}:00 ${ampm}`;
                        return (
                          <option key={i} value={timeString}>
                            {timeString}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Slot Status</label>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: true, isBooked: false })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          newSlot.isAvailable && !newSlot.isBooked
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Available
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewSlot({ ...newSlot, isAvailable: false, isBooked: true })}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                          newSlot.isBooked && !newSlot.isAvailable
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Booked
                      </button>
                    </div>
                  </div>

                  {/* Event Name Field - Only show for booked slots */}
                  {newSlot.isBooked && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Name (Optional)</label>
                      <input
                        type="text"
                        value={newSlot.eventName}
                        onChange={(e) => setNewSlot({ ...newSlot, eventName: e.target.value })}
                        placeholder="e.g., Math Tutoring Session, Student Meeting"
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between mt-6">
                <div className="flex space-x-2">
                  {!editingSlot && (
                    <button
                      onClick={handleMarkUnavailable}
                      className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Day Off
                    </button>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingSlot ? handleUpdateSlot : handleAddSlot}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (editingSlot ? 'Update' : 'Add Slot')}
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Tutor Schedules</h1>
        <p className="text-gray-600">View and manage tutor availability and schedules</p>
      </div>

      {/* Tutor Selection */}
      <div className="mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Tutor
          </label>
          <select
            value={selectedTutor?.id || ''}
            onChange={(e) => handleTutorChange(e.target.value)}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            disabled={loading}
          >
            <option value="">Choose a tutor...</option>
            {tutors.map(tutor => (
              <option key={tutor.id} value={tutor.id}>
                {tutor.name} ({tutor.email})
              </option>
            ))}
          </select>
          
          {selectedTutor && (
            <div className="mt-3 p-3 bg-blue-50 rounded-md">
              <div className="text-sm">
                <span className="font-medium text-blue-900">Selected Tutor:</span>
                <span className="ml-2 text-blue-700">{selectedTutor.name}</span>
              </div>
              <div className="text-sm text-blue-600">
                <span className="font-medium">Email:</span> {selectedTutor.email}
              </div>
              <div className="text-sm text-blue-600">
                <span className="font-medium">Timezone:</span> {selectedTutor.timezone}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {selectedTutor && (
        <div className="mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">Quick Actions</h3>
              <button
                onClick={copyLastWeek}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Copying...' : 'Copy Last Week'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="animate-spin h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Loading...</h3>
              <div className="mt-2 text-sm text-blue-700">Please wait while we fetch the data</div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar */}
      {selectedTutor && !loading && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedTutor.name}'s Schedule
            </h2>
            <div className="text-sm text-gray-600">
              Timezone: {scheduleData.timezone}
            </div>
          </div>
          {renderCalendar()}
        </div>
      )}

      {/* Legend */}
      {selectedTutor && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Legend</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-gray-700">Available Slots</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-gray-700">Booked Slots</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
              <span className="text-gray-700">Day Off</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2 ring-2 ring-blue-500"></div>
              <span className="text-gray-700">Today</span>
            </div>
          </div>
        </div>
      )}

      {/* Event Management Modal */}
      {renderEventModal()}
    </div>
  );
};

export default TutorSchedule;